"use client";

import React from "react";

export interface GqWebBlueprintBlockProps {
    tag?: string;
    title?: string;
    description?: string;

    // Left Box
    leftTag?: string;
    leftTitle?: string;
    leftDesc?: string;
    leftFooter?: string;

    // Middle Box
    centerTag?: string;
    centerTitle?: string;
    centerSub?: string;
    centerStats?: string[];
    centerFooter?: string;

    // Right Box
    rightTag?: string;
    pageModules?: { title: string; subtitle: string }[];
    rightFooter?: string;

    // Bottom Section
    systemLabel?: string;
    techItems?: string[];
}

export default function GqWebBlueprintBlock({
    tag = "02 / SITE ARCHITECTURE",
    title = "Page Structure & Content Blueprint",
    description = "A systematic breakdown of the marketing website architecture, mapping every content section to its conversion goal and user flow logic.",

    leftTag = "Brand Narrative",
    leftTitle = "Editorial Content",
    leftDesc = "We wrote long-form editorial copy across 8 website chapters, translating complex SaaS features into warm, accessible prose that property hosts can relate to immediately.",
    leftFooter = "8 Chapters · 6,000+ Words",

    centerTag = "Conversion Flows",
    centerTitle = "FREE TRIAL FUNNEL",
    centerSub = "Lead Capture → Onboarding → Guidebook",
    centerStats = ["Pricing Tiers: 4 Plans", "Testimonials: 6 Verified Hosts"],
    centerFooter = "Primary CTA: Begin Free Trial",

    rightTag = "Page Modules",
    pageModules = [
        { title: "Hero & Method", subtitle: "Brand Introduction" },
        { title: "Pricing Grid", subtitle: "Transparent Plans" }
    ],
    rightFooter = "PRODUCTION LIVE",

    systemLabel = "MARKETING WEBSITE: EDITORIAL-DRIVEN CONVERSION ARCHITECTURE",
    techItems = ["NEXT.JS 15", "TAILWIND CSS", "FIREBASE HOSTING"]
}: GqWebBlueprintBlockProps) {
    return (
        <section className="py-28 px-6 md:px-12 lg:px-24 bg-[#FDFBF8] border-b border-[#EDE6DD] text-[#444] font-sans antialiased w-full">
            <div className="container mx-auto">
                <div className="max-w-3xl mb-16">
                    {tag && (
                        <span className="text-xs font-mono text-[#D4A574] tracking-widest block mb-2 uppercase font-semibold">
                            {tag}
                        </span>
                    )}
                    {title && (
                        <h2 className="text-4xl font-light text-[#1A1A1A] leading-tight">
                            {title}
                        </h2>
                    )}
                    {description && (
                        <p className="text-zinc-500 text-sm mt-3 font-light max-w-xl">
                            {description}
                        </p>
                    )}
                </div>

                {/* The Grid Blueprint Card */}
                <div className="w-full bg-[#FAF8F5] border border-[#EDE6DD] rounded-2xl p-6 md:p-8 lg:p-12 relative overflow-hidden shadow-sm">
                    
                    {/* Ruler Markings */}
                    <div className="absolute top-0 left-12 right-12 h-6 border-b border-[#EDE6DD] flex justify-between items-center text-[9px] font-mono text-zinc-400 select-none pointer-events-none">
                        <span>0px</span>
                        <span>305px</span>
                        <span>626px</span>
                        <span>947px</span>
                        <span>1268px</span>
                        <span>1440px</span>
                    </div>
                    <div className="absolute left-0 top-12 bottom-12 w-6 border-r border-[#EDE6DD] flex flex-col justify-between items-center text-[9px] font-mono text-zinc-400 py-2 select-none pointer-events-none">
                        <span>Y:0</span>
                        <span>Y:200</span>
                        <span>Y:400</span>
                        <span>Y:600</span>
                    </div>

                    {/* Blueprint Mockup */}
                    <div className="pt-8 grid grid-cols-1 md:grid-cols-12 gap-6 relative z-10">
                        
                        {/* Left block */}
                        <div className="md:col-span-4 border border-dashed border-[#D4A574]/30 bg-[#D4A574]/3 rounded p-6 relative flex flex-col justify-between h-80">
                            {leftTag && (
                                <div className="absolute -top-3 left-4 bg-[#FAF8F5] px-2 py-0.5 rounded border border-[#EDE6DD] text-[8px] font-mono text-[#D4A574]">
                                    {leftTag}
                                </div>
                            )}
                            <div className="space-y-4">
                                <span className="text-[10px] font-mono text-[#D4A574] tracking-widest block font-semibold">// COPY STRATEGY</span>
                                {leftTitle && <h3 className="text-2xl font-light text-[#1A1A1A] italic">{leftTitle}</h3>}
                                {leftDesc && <p className="text-zinc-650 text-[11px] leading-relaxed font-light">{leftDesc}</p>}
                            </div>
                            {leftFooter && (
                                <div className="text-[8px] font-mono text-zinc-450 font-medium">
                                    {leftFooter}
                                </div>
                            )}
                        </div>

                        {/* Middle block */}
                        <div className="md:col-span-5 border border-dashed border-[#D4A574]/30 bg-[#D4A574]/3 rounded p-6 relative flex flex-col justify-between h-80">
                            {centerTag && (
                                <div className="absolute -top-3 left-4 bg-[#FAF8F5] px-2 py-0.5 rounded border border-[#EDE6DD] text-[8px] font-mono text-[#D4A574]">
                                    {centerTag}
                                </div>
                            )}
                            <div className="space-y-4">
                                <div className="h-28 rounded bg-white border border-[#EDE6DD] flex flex-col items-center justify-center font-mono text-[9px] text-[#D4A574] relative overflow-hidden p-4 space-y-2 shadow-xs">
                                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#D4A57408_1px,transparent_1px),linear-gradient(to_bottom,#D4A57408_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none" />
                                    {centerTitle && <div className="text-[10px] font-bold text-[#D4A574]">{centerTitle}</div>}
                                    {centerSub && <div className="text-[8px] text-zinc-400">{centerSub}</div>}
                                </div>
                                {centerStats && centerStats.length >= 2 && (
                                    <div className="flex justify-between items-center text-[10px] font-mono">
                                        <span className="text-zinc-550">{centerStats[0]}</span>
                                        <span className="text-[#D4A574] font-semibold">{centerStats[1]}</span>
                                    </div>
                                )}
                            </div>
                            {centerFooter && (
                                <div className="text-[8px] font-mono text-zinc-450">
                                    {centerFooter}
                                </div>
                            )}
                        </div>

                        {/* Right block */}
                        <div className="md:col-span-3 border border-dashed border-[#D4A574]/30 bg-[#D4A574]/3 rounded p-6 relative flex flex-col justify-between h-80">
                            {rightTag && (
                                <div className="absolute -top-3 left-4 bg-[#FAF8F5] px-2 py-0.5 rounded border border-[#EDE6DD] text-[8px] font-mono text-[#D4A574]">
                                    {rightTag}
                                </div>
                            )}
                            <div className="space-y-4">
                                {pageModules && pageModules.map((c, idx) => (
                                    <div key={idx} className="p-3 bg-white border border-[#EDE6DD] rounded text-center shadow-xs">
                                        <span className="text-[10px] font-mono text-[#D4A574] block font-bold">{c.title}</span>
                                        <span className="text-[8px] font-mono text-zinc-400 block uppercase">{c.subtitle}</span>
                                    </div>
                                ))}
                            </div>
                            {rightFooter && (
                                <div className="w-full py-2 border border-[#EDE6DD] text-[#D4A574] bg-[#D4A574]/5 font-mono text-[9px] text-center uppercase tracking-wider font-semibold rounded">
                                    {rightFooter}
                                </div>
                            )}
                        </div>

                    </div>

                    {/* Extra Layout spec details */}
                    <div className="mt-8 border-t border-[#EDE6DD] pt-6 flex flex-wrap justify-between items-center text-[10px] font-mono text-zinc-400 gap-4">
                        {systemLabel && <div>{systemLabel}</div>}
                        {techItems && techItems.length > 0 && (
                            <div className="flex gap-4">
                                <span>STACK:</span>
                                {techItems.map((o, idx) => (
                                    <span key={idx}>{o}</span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
